import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RoleBasedVisibilityDirective } from '../../../../../../../directives/role-based-visibility.directive';
import { ActivityEditDialogData, ActivityTripComponent } from '../../../../../../../models';
import { InputFiscalDataComponent } from '../../../../../../shared/input-fiscal-data/input-fiscal-data.component';
import { InputMonetaryAmountComponent } from '../../../../../../shared/input-monetary-amount/input-monetary-amount.component';
import { InputTimeComponent } from '../../../../../../shared/input-time/input-time.component';
import { ValidationErrorBarComponent } from '../../../../../../shared/validation-error-bar/validation-error-bar.component';
import { bothOrNoneFieldsValidator } from '../../../../../../validators/both-or-none.validator';
import { ActivityEditDialogBase } from '../../activity-edit-dialog-base';

@Component({
  selector: 'app-trip-component-edit-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatDividerModule,
    MatSelectModule,
    MatButtonModule,
    DragDropModule,
    TranslateModule,
    InputTimeComponent,
    ValidationErrorBarComponent,
    InputMonetaryAmountComponent,
    InputFiscalDataComponent,
    RoleBasedVisibilityDirective,
  ],
  templateUrl: './trip-component-edit-dialog.component.html'
})
export class TripComponentEditDialogComponent extends ActivityEditDialogBase<ActivityTripComponent> implements OnInit {

  form!: FormGroup;

  constructor(
    dialogRef: MatDialogRef<TripComponentEditDialogComponent>,
    confirmationDialog: MatDialog,
    translateService: TranslateService,
    @Inject(MAT_DIALOG_DATA) override data: ActivityEditDialogData
  ) {
    super(dialogRef, confirmationDialog, translateService, data);
  }
  ngOnInit(): void {

    this.initForm();


    if (this.data.activityData) {
      this.fillFormFields(this.data.activityData as ActivityTripComponent);
    }

    // Set up dynamic validation after form initialization
    this.setupDynamicValidation();

  }

  initForm(): void {
    this.form = new FormGroup({
      name: new FormControl('', [Validators.required]),
      startTime: new FormControl(null),
      endTime: new FormControl(null),
      totalPrice: new FormControl(null, [Validators.min(0)]),
      description: new FormControl(''),
      advancePurchaseDays: new FormControl(''),
      notes: new FormControl(''),
      disclaimer: new FormControl(''),
      ivaCode: new FormControl(''),
      fiscalAddress: new FormControl(''),
      legalName: new FormControl(''),
      type: new FormControl('trip-component'),
    }, {
      // start and end time are optional, but if one is set the other one is required too
      validators: bothOrNoneFieldsValidator('startTime', 'endTime')
    });
  }

  private fillFormFields(data: ActivityTripComponent): void {
    this.form.patchValue({
      name: data.name,
      startTime: data.startTime,
      endTime: data.endTime,
      totalPrice: data.totalPrice,
      description: data.description,
      notes: data.notes,
      disclaimer: data.disclaimer,
      advancePurchaseDays: data.advancePurchaseDays,
      ivaCode: data.ivaCode,
      fiscalAddress: data.fiscalAddress,
      legalName: data.legalName,
    })

  }

  confirm(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const activityData: ActivityTripComponent = this.form.getRawValue() as ActivityTripComponent;
    this.confirmAndCloseDialog(activityData);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  toUppercase(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.toUpperCase();
    // force to update the partita iva in uppercase
    this.form.get('ivaCode')?.setValue(input.value, { emitEvent: false });
  }

  /**
 * Sets up dynamic validation for fiscal fields based on totalPrice
 */
  private setupDynamicValidation(): void {
    const totalPriceControl = this.form.get('totalPrice');
    const fiscalFields = ['advancePurchaseDays', 'ivaCode', 'fiscalAddress', 'legalName'];

    // Listen to totalPrice changes
    totalPriceControl?.valueChanges.subscribe(value => {
      this.updateFiscalFieldValidators(value, fiscalFields);
    });

    // Set initial validators based on current totalPrice value
    const currentTotalPrice = totalPriceControl?.value;
    this.updateFiscalFieldValidators(currentTotalPrice, fiscalFields);
  }

  /**
   * Updates validators for fiscal fields based on total price. If price is > 0 add the validator
   * otherwise remove them. 
   */
  private updateFiscalFieldValidators(totalPrice: number, fiscalFields: string[]): void {
    const shouldBeRequired = totalPrice > 0;

    fiscalFields.forEach(fieldName => {
      const control = this.form.get(fieldName);
      if (control) {
        // Clear existing validators
        control.clearValidators();

        // Add validators based on condition
        if (shouldBeRequired) {
          if (fieldName === 'ivaCode') {
            // IVA code has specific length requirements (11 characters for Italian VAT)
            control.setValidators([
              Validators.required,
              Validators.minLength(11),
              Validators.maxLength(11),
            ]);
          } else {
            control.setValidators([Validators.required]);
          }
        }

        // Update validity
        control.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  /**
 * Helper method to check if fiscal fields should be required
 * Useful for template conditional validation messages
 */
  shouldFiscalFieldsBeRequired(): boolean {
    const totalPrice = this.form.get('totalPrice')?.value;
    return totalPrice > 0;
  }

}
